import { InjectionToken } from '@angular/core';

export interface AumicoAbcpConfig {
    apiGateway: string;
}

/** Mirrors `AumicoIntegrationsConfigService`; supplied by the app via `AbcpDataModule.forRoot`. */
export const AumicoAbcpConfigService = new InjectionToken<AumicoAbcpConfig>('AumicoAbcpConfigService');

/**
 * The cadence the two reconciliation polls run at — #162 (AP8a): the account-status poll
 * (`ProjectAccountsEffects.pollReconciliation$`) and the post-upload document poll
 * (`ClosingDocumentEffects.pollAfterUpload$`). One shape for both, because they are the same kind of
 * self-scheduling `timer` chain (spec:GH-162 S7) and one injected value lets a test shorten both at
 * once.
 *
 * ⚠️ WHERE THE OVERRIDE IS WIRED (spec:GH-162 S3): NOT here. `environment` exists only in
 * `projects/aumico-project-reports`, so this library's default below is the PRODUCTION cadence, full
 * stop — the app overrides it in its own provider list (`app.module.ts`), behind
 * `environment.production === false`, reading `window.__aumicoAbcpPollIntervals` — the same shape and
 * key `e2e/fixtures/abcp-reconciliation.ts`'s `shortenPollIntervals` writes before the app boots.
 */
export interface AumicoAbcpPollIntervals {
    /** The tick while the run may be about to answer. */
    fastMs: number;
    /** The tick afterwards — in production, thirty seconds from the FIRST tick. */
    slowMs: number;
    /** How long the fast cadence lasts, in ms. */
    fastWindowMs: number;
    /**
     * The hard ceiling, IN TICKS rather than wall-clock ms (plan:GH-162 P10): there is no unit level
     * for an effect in this workspace, so every poll case is wall clock, and a ms ceiling at production
     * cadence would be a ten-minute test.
     */
    maxTicks: number;
}

/**
 * Ten seconds fast, thirty seconds afterwards — matches `pollClassification$`
 * (`project-accounts.effects.ts`). `maxTicks: 24` is the tick count that covers roughly the same
 * ten-minute give-up window `CLASSIFICATION_GIVE_UP_MS` uses (6 ticks in the first 60 s, 18 more at
 * 30 s each).
 */
export const AUMICO_ABCP_PRODUCTION_POLL_INTERVALS: AumicoAbcpPollIntervals = {
    fastMs: 10_000,
    slowMs: 30_000,
    fastWindowMs: 60_000,
    maxTicks: 24,
};

export const AumicoAbcpPollIntervalsToken = new InjectionToken<AumicoAbcpPollIntervals>(
    'AumicoAbcpPollIntervals',
    { providedIn: 'root', factory: () => AUMICO_ABCP_PRODUCTION_POLL_INTERVALS },
);

/**
 * The FLOOR an accepted override may not go below on its FAST tick — #162 round 4 (SRR F3).
 *
 * The app's provider (`app.module.ts`) reads `window.__aumicoAbcpPollIntervals` verbatim today: `??`
 * catches only `null`/`undefined`, so `{fastMs: 0}` typed into a developer console reaches both poll
 * chains and turns a capped background poll into a tight request loop against a route that has no rate
 * limiter of its own (#160 F8). 1000 ms is not a security boundary — reaching this seam already needs
 * script execution on the page, which is a serious incident on its own — it is the floor that makes the
 * seam useless as an amplifier.
 */
export const AUMICO_ABCP_POLL_INTERVAL_FLOOR_MS = 1_000;

/**
 * Is `candidate` a well-formed override at or above the floor? Every field has to be a finite number
 * (rules out the missing field, `NaN`, and anything that is not a number at all), and BOTH tick
 * intervals, `fastMs` and `slowMs`, have to be at or above {@link AUMICO_ABCP_POLL_INTERVAL_FLOOR_MS}
 * (rules out zero and negative). `fastWindowMs` and `maxTicks` carry no floor of their own: they choose
 * WHICH interval applies and HOW OFTEN, never how short a tick is.
 *
 * REV-22118 (Code-Review R2 R8) — round 4 / SRR F3 pinned only `fastMs`. `{slowMs: 0, fastWindowMs: 0}`
 * then skipped the fast window and ticked at `timer(0)`: the tight loop the floor exists to prevent,
 * reached through the other interval. It mattered little while the override lived only in dev builds;
 * since REV-22118 the testing build honours it too.
 */
function isWellFormedPollIntervals(candidate: unknown): candidate is AumicoAbcpPollIntervals {
    if (candidate === null || typeof candidate !== 'object') {
        return false;
    }

    const value = candidate as Record<string, unknown>;

    return (
        Number.isFinite(value['fastMs']) &&
        Number.isFinite(value['slowMs']) &&
        Number.isFinite(value['fastWindowMs']) &&
        Number.isFinite(value['maxTicks']) &&
        (value['fastMs'] as number) >= AUMICO_ABCP_POLL_INTERVAL_FLOOR_MS &&
        (value['slowMs'] as number) >= AUMICO_ABCP_POLL_INTERVAL_FLOOR_MS
    );
}

/**
 * What the app's dev-only override provider (`app.module.ts`) hands the token: `candidate` if it is
 * well-formed and at or above the floor, the PRODUCTION cadence otherwise. The one place this decision
 * is made, so `shortenPollIntervals` in the e2e fixture stays the one place a test drives it from.
 */
export function resolveAumicoAbcpPollIntervals(candidate: unknown): AumicoAbcpPollIntervals {
    return isWellFormedPollIntervals(candidate) ? candidate : AUMICO_ABCP_PRODUCTION_POLL_INTERVALS;
}
